/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IEveQuickOrderData } from './eve-quick-order.data';
import { IEveQuickOrderProps } from './eve-quick-order.props.autogenerated';
import { EveQuickOrderRow, IEveQuickOrderRowResources } from './components/EveQuickOrderRow';
import { EveQuickOrderButtonRow, IEveQuickOrderButtonRowResources } from './components/EveQuickOrderButtonRow';
import { debounce } from 'lodash';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { AsyncResult, CommerceProperty, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { EveQuickOrderHeader, IEveQuickOrderHeaderResources } from './components/EveQuickOrderHeader';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import { IOrderUploadItem } from '../order-upload/order-upload';
import {
    getAdvancedNotesAsync,
    getInventoryLevelRangesAsync,
    getProductsByManufacturerItemIdAsync
} from '../../actions/DataActionExtension.g';
import {
    IAdvancedNoteEntity,
    IManufacturerCodeResponseEntity,
    IProfileDataMap,
    ISearchTextProductMap
} from '../../actions/DataServiceEntities.g';
import { CommercePropertyValueEnum } from '../allowable-freight/utils/CommercePropertyValueEnum';
import GetProductEligibilityAction, { GetProductEligibilityInput } from '../../actions/get-product-eligibility.action';
import { SupersessionKey } from '../../themes/gardner-theme/models/SupersessionKey';
import { getPriceForProductSearchResultAction, PriceForProductSearchResultInput } from '../../actions/get-price-products-search-results';
export interface IEveQuickOrderViewProps extends IEveQuickOrderProps<IEveQuickOrderData> {
    quickOrderTable: React.ReactElement;
    quickOrderHeader: React.ReactElement;
    quickOrderWrapper: IModuleProps;
    handleUploadedItems: (items: IOrderUploadItem[]) => void;
    orderUploadWrapper: INodeProps;
    guideText: React.ReactElement | undefined;
}

export interface IEveQuickOrderRowData {
    id: number;
    value: string;
    quantity: string;
    product: SimpleProductExt | null;
    availableProducts: SimpleProductExt[];
    rowError: RowError;
    quantityErrors: string[];
    manufacturerCode: string;
    advancedNotes: IAdvancedNoteEntity[];
}

interface IEveQuickOrderState {
    rows: IEveQuickOrderRowData[];
    // @ts-expect-error
    debounce: debounce;
    loading: boolean;
    prices: SimpleProductExt[];
    hasUpdated: boolean;
    updatedRows: Set<number>;
    shouldRowUpdate: boolean;
    uploadedItems: boolean;
}

export enum RowError {
    ProductNotFound,
    InvalidQuantity,
    MultipleChoicesAvailable,
    NotEligbile,
    None,
    Unavailable,
    ZeroPrice
}

export interface ISearchTextProductMapExt extends ISearchTextProductMap {
    SearchText?: string;
    Products?: SimpleProductExt[];
}

export interface SimpleProductExt extends SimpleProduct {
    IsEligible?: boolean;
    IsUnavailable?: boolean;
}

interface EligibleItem {
    ItemId: string;
    IsEligible: boolean;
    IsAssorted?: boolean;
}

export interface EligibleItems extends Array<EligibleItem> {}

/**
 * Enables users to quickly order products using item ids
 * EveQuickOrder component
 * @extends {React.PureComponent<IEveQuickOrderProps<IEveQuickOrderData>>}
 */
class EveQuickOrder extends React.PureComponent<IEveQuickOrderProps<IEveQuickOrderData>, IEveQuickOrderState> {
    private _defaultMinQueryLength = 4;
    private _manufacturerCodeExtensionPropertyKey = 'ManufacturerCode';
    private readonly _bulkUploadChunkSize = 50;
    private readonly _bulkUploadConcurrency = 2;
    private readonly _bulkUploadMaxAttempts = 2;

    constructor(props: IEveQuickOrderProps<IEveQuickOrderData>) {
        super(props);

        this._handleRowDelete = this._handleRowDelete.bind(this);
        this._generateRow = this._generateRow.bind(this);
        this._renderTable = this._renderTable.bind(this);
        this._updateRowValue = this._updateRowValue.bind(this);
        this._addTwoRows = this._addTwoRows.bind(this);
        this._handleUploadedItems = this._handleUploadedItems.bind(this);
        this._tableInit = this._tableInit.bind(this);
        this._handleAddToCart = this._handleAddToCart.bind(this);
        this._handleSelectedProductChange = this._handleSelectedProductChange.bind(this);

        this.state = {
            rows: [],
            debounce: this._initDebounceFn(),
            loading: false,
            prices: [],
            hasUpdated: false,
            updatedRows: new Set(),
            shouldRowUpdate: false,
            uploadedItems: false
        };
    }

    public componentDidMount() {
        this._tableInit();
    }

    /**
     * Initialises the table to the module configuration
     * @private
     */
    private _tableInit() {
        const {
            config: { defaultRows }
        } = this.props;
        const rows: IEveQuickOrderRowData[] = [];
        for (let i = 0; i < defaultRows; i++) {
            rows.push(this._generateEmptyRow(i + 1));
        }
        this.setState({
            rows,
            prices: [],
            hasUpdated: true,
            updatedRows: new Set(),
            shouldRowUpdate: false,
            uploadedItems: false
        });
    }

    /**
     * Initializes the debounce function for all input forms
     * @private
     */
    private _initDebounceFn() {
        return debounce(async (data: { searchText: string[]; id: number }) => {
            // Ticket 7920: Unexpected Quick Order Response
            // Find the row that matches the ID to get the previous manufacturer code
            const rows = [...this.state.rows];
            const idx = rows.findIndex(row => row.id === data.id);

            if (idx < 0) {
                return;
            }

            const previousManufacturerCode = rows[idx].manufacturerCode || '';

            // Clean the searchText by stripping out any previous manufacturer code.
            const cleanedSearchText = [this._stripManufacturerFromSearchText(previousManufacturerCode, data.searchText[0])];

            const responseData = await this.getProductSearchResult(cleanedSearchText);
            if (ArrayExtensions.hasElements(responseData) && ArrayExtensions.hasElements(responseData[0].Products)) {
                const notes = await getAdvancedNotesAsync(
                    {
                        callerContext: this.props.context.actionContext
                    },
                    [responseData[0].Products[0]?.ItemId!]
                );

                this._updateRowProduct(data.id, responseData[0].Products, notes || [], cleanedSearchText[0], rows[idx].quantity);
            } else {
                rows[idx].rowError = this._updateRowErrorState(rows[idx]);
                this.setState({ rows, shouldRowUpdate: false });
            }
        }, 500);
    }

    /**
     * Adds all valid items to cart
     * @private
     */
    private async _handleAddToCart() {
        const products = this.state.rows.filter(row => !!row.product && row.rowError === RowError.None);
        // 6321 - Resolve issue when the same item is added to the quick order form more than once
        const dummyProducts = [...products];
        const combinedProducts = dummyProducts.reduce(
            (acc: IEveQuickOrderRowData[], cur: IEveQuickOrderRowData): IEveQuickOrderRowData[] => {
                const row = acc.length > 0 && acc.find(({ product }) => product?.ItemId === cur.product?.ItemId);
                if (row) {
                    row.quantity = (parseInt(row.quantity, 10) + parseInt(cur.quantity, 10)).toString();
                } else {
                    acc.push(cur);
                }
                return acc;
            },
            []
        );

        if (combinedProducts.length) {
            const cartState = this.props.data.cart.result;
            const input = combinedProducts.map(product => {
                return {
                    product: product.product!,
                    count: parseInt(product.quantity, 10),
                    catalogId: 0
                };
            });
            if (cartState) {
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0 });
                }
                this.setState({ loading: true });
                await cartState.addProductsToCart(input);
                this.setState({ loading: false });
                this._tableInit();
            }
        }
    }

    /**
     * Gets product by item id
     * @param data item ids
     * @private
     */
    private async getProductSearchResult(data: string[]): Promise<ISearchTextProductMapExt[]> {
        const itemIds = Array.from(
            new Map<string, string>(
                data
                    .map(itemId => itemId?.toString().trim())
                    .filter((itemId): itemId is string => Boolean(itemId))
                    .map(itemId => [itemId.toUpperCase(), itemId] as [string, string])
            ).values()
        );

        if (itemIds.length === 0) {
            return [];
        }

        const {
            context: { actionContext }
        } = this.props;

        const response: ISearchTextProductMapExt[] = await getProductsByManufacturerItemIdAsync(
            { callerContext: actionContext },
            itemIds,
            ''
        );

        const uniqueProductItemIds = new Map<string, string>();

        response.forEach(result => {
            result.Products?.forEach(product => {
                const itemId = product.ItemId?.trim();
                if (itemId) {
                    uniqueProductItemIds.set(itemId.toUpperCase(), itemId);
                }
            });
        });

        const productItemIds = Array.from(uniqueProductItemIds.values());
        if (productItemIds.length === 0) {
            return response;
        }

        const cartId = this.props.data.cart?.result?.cart?.Id || '';
        const eligibilityInput = new GetProductEligibilityInput(productItemIds, false, cartId, null, null, null);
        const eligibilityData = GetProductEligibilityAction(eligibilityInput, actionContext);
        const inventoryRanges: AsyncResult<IProfileDataMap[]> = getInventoryLevelRangesAsync(
            { callerContext: actionContext },
            productItemIds
        );

        await Promise.all([eligibilityData, inventoryRanges]);

        const eligibilityByItemId = new Map<string, EligibleItem>();
        eligibilityData.result?.forEach((item: any) => {
            const itemId = this._getExtensionPropertyValueByKey(item.ExtensionProperties, 'ItemId', CommercePropertyValueEnum.string);

            if (itemId) {
                eligibilityByItemId.set(String(itemId).toUpperCase(), {
                    ItemId: String(itemId),
                    IsEligible: item.IsEligible,
                    IsAssorted: item.IsAssorted
                });
            }
        });

        const oosUpperBoundByItemId = new Map<string, number>();
        inventoryRanges.result?.forEach(profileData => {
            profileData.Profile?.forEach(profile => {
                if (profile.ItemId && profile.Code === 'OOS') {
                    oosUpperBoundByItemId.set(profile.ItemId.toUpperCase(), profile.UpperBoundInclusive ?? 0);
                }
            });
        });

        response.forEach(result => {
            result.Products?.forEach(product => {
                const itemIdKey = product.ItemId?.toUpperCase() || '';
                const eligibility = eligibilityByItemId.get(itemIdKey);

                if (eligibility) {
                    product.IsEligible = eligibility.IsEligible;
                    product.IsUnavailable = !eligibility.IsAssorted;
                } else {
                    product.IsEligible = false;
                }

                const currentInventoryText = this.getProductSupersessionProperty(product, SupersessionKey.Inventory) || '0';
                const currentInventory = parseInt(currentInventoryText, 10) || 0;
                const oosUpperBound = oosUpperBoundByItemId.get(itemIdKey);

                if (oosUpperBound !== undefined) {
                    product.IsUnavailable = currentInventory <= oosUpperBound;
                }
            });
        });

        return response;
    }

    private getProductSupersessionProperty = (product: SimpleProductExt, extensionKey: SupersessionKey) => {
        const properties = product.ExtensionProperties;
        if (properties) {
            return properties.find(pr => pr.Key === extensionKey)?.Value?.StringValue || null;
        }
        return null;
    };

    /**
     * Gets manufacturer codes from data
     * @private
     */
    private _getManufacturerCodeList = (row: IEveQuickOrderRowData): IManufacturerCodeResponseEntity[] => {
        const codesResults = this.props.data.manufacturerCodes.result ? this.props.data.manufacturerCodes.result : [];

        //@ts-ignore
        const manufacturerCodes: IManufacturerCodeResponseEntity[] = codesResults.manufacturerCodes;

        const hiddenManufacturerCodes: string[] = this.props.config.hiddenManufacturerCodes || [];

        if (row.availableProducts.length) {
            const availableCodes = row.availableProducts.map(p => this._getManufacturerCodeForRow([p]));

            // filter out hidden manufacturere codes
            return manufacturerCodes.filter(
                code =>
                    availableCodes.includes(code.GDNManufacturerCode || '') &&
                    !hiddenManufacturerCodes.includes(code.GDNManufacturerCode || '')
            );
        }

        // Filter out hidden manufacturer codes
        return manufacturerCodes.filter(code => !hiddenManufacturerCodes.includes(code.GDNManufacturerCode!));
    };

    /**
     * Renders the header of the page
     * @private
     */
    private _renderHeading() {
        const {
            resources: { addAllToCartText, clearFormText },
            config: { helpText, title }
        } = this.props;
        const resources: IEveQuickOrderHeaderResources = {
            addAllToCartText: addAllToCartText,
            clearAllText: clearFormText
        };
        return (
            <EveQuickOrderHeader
                heading={title}
                helpText={helpText || ''}
                resources={resources}
                canAddToCart={this._getAllAddedProducts().length}
                handleClearAll={this._tableInit}
                handleAddToCart={this._handleAddToCart}
            />
        );
    }

    /**
     * Retrieves the value of an extension property using the given key
     * @private
     * @param {(CommerceProperty[] | undefined)} properties
     * @param {string} key
     * @param {CommercePropertyValueEnum} valueType
     * @memberof EveQuickOrder
     * @return the extension property value associated with the given key
     */
    private _getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    /**
     * Renders the add to bag and reset form button row
     * @private
     */
    private _renderButtonRow() {
        const {
            resources: { addAllToCartText, clearFormText, addMoreRowsText }
        } = this.props;
        const resources: IEveQuickOrderButtonRowResources = {
            addAllToCartText: addAllToCartText,
            clearAllText: clearFormText,
            addMoreRowsText: addMoreRowsText
        };
        const { courseInCartText, invoiceInCartText } = this.props.resources;
        let courseId: string | number | boolean | null | undefined;
        if (this.props.data.cart.result?.hasInvoiceLine) {
            return (
                <div className='eve-button-row--error'>
                    <div className='eduerrors'>{invoiceInCartText}</div>
                </div>
            );
        }
        const cart = this.props.data.cart.result?.cart;
        const cartlines = cart?.CartLines;
        if (cartlines && cartlines.length) {
            courseId = this._getExtensionPropertyValueByKey(cartlines[0].ExtensionProperties, 'CourseId', CommercePropertyValueEnum.string);
        }

        if (courseId) {
            return (
                <div className='eve-button-row--error'>
                    <div className='eduerrors'>{courseInCartText}</div>
                </div>
            );
        }

        let isRowError: boolean = false;
        for (const row of this.state.rows) {
            if (row.rowError !== RowError.None) {
                isRowError = true;
                break;
            }
        }

        return (
            <EveQuickOrderButtonRow
                resources={resources}
                canAddToCart={!isRowError}
                handleClearAll={this._tableInit}
                handleAddToCart={this._handleAddToCart}
                isLoading={this.state.loading}
            />
        );
    }

    /**
     * gets all products
     * @private
     */
    private _getAllAddedProducts() {
        return this.state.rows.map(row => row.product?.RecordId).filter(pr => !!pr);
    }

    /**
     * Generates a row component using row data
     * @param row row data to generate from
     * @param isLast whether its the last row in the form
     * @private
     */
    private _generateRow(row: IEveQuickOrderRowData, isLast: boolean) {
        const resources: IEveQuickOrderRowResources = {
            defaultManufacturerCodeText: this.props.resources.defaultDropdownText,
            deleteText: this.props.resources.deleteHeading,
            productEligibilityText: this.props.resources.productEligibilityText,
            supersededProductText: this.props.resources.supersededProductText
        };
        const matchingProduct = this.state.prices.find(product => product.ItemId === row.product?.ItemId);
        const yourPrice = matchingProduct ? matchingProduct.BasePrice : null;

        return (
            <EveQuickOrderRow
                yourPrice={yourPrice}
                id={row.id}
                onDelete={this._handleRowDelete}
                onRowChange={this._updateRowValue}
                onAddRows={this._addTwoRows}
                value={row.value}
                quantity={row.quantity}
                product={row.product}
                availableProducts={row.availableProducts}
                currencyFormatter={this.props.context.cultureFormatter.formatCurrency}
                isLastRow={isLast}
                rowError={row.rowError}
                quantityErrors={row.quantityErrors}
                onSelectedProductChange={this._handleSelectedProductChange}
                manufacturerCodes={this._getManufacturerCodeList(row)}
                resources={resources}
                manufacturerCode={row.manufacturerCode}
                advancedNotes={row.advancedNotes}
                customerAccNum={this.props.context.request.user.customerAccountNumber}
                config={this.props.context.app.config}
                isSobo={this.props.context.request.user.isOBORequest || false}
                context={this.props.context.actionContext}
                freePriceMask={this.props.context.app.config.freePriceMask || '----------'}
            />
        );
    }

    /**
     * Handles the product in a row changing
     *
     * @private
     * @param {number} id to change
     * @param {number} productId product which has been selected
     * @memberof EveQuickOrder
     */
    private _handleSelectedProductChange(id: number, productId: number) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(row => row.id === id);

        if (idx < 0) {
            return;
        }

        rows[idx].product = rows[idx].availableProducts.find(product => product.RecordId === productId) || rows[idx].product;

        this.setState(
            prevState => {
                const updatedRows = new Set(prevState.updatedRows);
                updatedRows.add(id);

                return {
                    rows,
                    updatedRows,
                    shouldRowUpdate: true
                };
            },
            () => {
                void this.getUpdatedProductPrice();
            }
        );
    }

    private async _mapWithConcurrency<TInput, TOutput>(
        values: TInput[],
        concurrency: number,
        worker: (value: TInput, index: number) => Promise<TOutput>
    ): Promise<TOutput[]> {
        if (values.length === 0) {
            return [];
        }

        const results = new Array<TOutput>(values.length);
        const workerCount = Math.min(Math.max(concurrency, 1), values.length);
        let nextIndex = 0;
        let failure: unknown;

        const executeWorker = async (): Promise<void> => {
            while (failure === undefined) {
                const currentIndex = nextIndex++;
                if (currentIndex >= values.length) {
                    return;
                }

                try {
                    results[currentIndex] = await worker(values[currentIndex], currentIndex);
                } catch (error) {
                    failure = error;
                    throw error;
                }
            }
        };

        await Promise.all(Array.from({ length: workerCount }, () => executeWorker()));
        return results;
    }

    private async _retryTransientRequest<T>(
        operation: () => Promise<T>,
        batchNumber: number,
        maxAttempts: number = this._bulkUploadMaxAttempts
    ): Promise<T> {
        let lastError: unknown;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            const startedAt = Date.now();

            try {
                const result = await operation();
                console.info(`Quick Order batch ${batchNumber} completed in ${Date.now() - startedAt} ms on attempt ${attempt}.`);
                return result;
            } catch (error) {
                lastError = error;

                if (!this._isTransientRequestError(error) || attempt === maxAttempts) {
                    throw error;
                }

                const delayMs = 1000 * attempt + Math.floor(Math.random() * 500);
                console.warn(`Quick Order batch ${batchNumber} failed on attempt ${attempt}. Retrying in ${delayMs} ms.`, error);

                await new Promise<void>(resolve => setTimeout(resolve, delayMs));
            }
        }

        throw lastError;
    }

    private _isTransientRequestError(error: unknown): boolean {
        const candidate = error as {
            status?: number;
            statusCode?: number;
            message?: string;
            response?: { status?: number };
        };

        const status = candidate?.status ?? candidate?.statusCode ?? candidate?.response?.status;
        const message = candidate?.message?.toLowerCase() ?? '';

        return (
            status === 408 ||
            status === 429 ||
            status === 500 ||
            status === 502 ||
            status === 503 ||
            status === 504 ||
            message.includes('timeout') ||
            message.includes('timed out')
        );
    }

    private _normalizeSearchText(value: string | undefined): string {
        if (value === undefined || value === null) {
            return '';
        }

        return value
            .toString()
            .trim()
            .toUpperCase();
    }

    private _getUploadedItemSearchText(item: IOrderUploadItem): string {
        const manufacturer = item.manufacturer?.toString() ?? '';
        const partNumber = item.part_number?.toString() ?? '';

        return `${manufacturer}${partNumber}`.trim();
    }

    private _buildUploadedItemResponseLookup(
        chunks: string[][],
        batchResponses: ISearchTextProductMapExt[][]
    ): Map<string, ISearchTextProductMapExt> {
        const lookup = new Map<string, ISearchTextProductMapExt>();

        batchResponses.forEach((batchResponse, batchIndex) => {
            const requestedChunk = chunks[batchIndex] ?? [];

            requestedChunk.forEach((requestedItemId, itemIndex) => {
                const requestedKey = this._normalizeSearchText(requestedItemId);
                if (!requestedKey) {
                    return;
                }

                const exactMatch = batchResponse.find(result => this._normalizeSearchText(result.SearchText) === requestedKey);

                // GetProductsByItemIdService returns one response entry for each requested
                // search text and preserves the request order. Use that positional mapping as
                // a fallback in case SearchText is transformed or omitted by the response.
                const positionalMatch = batchResponse.length === requestedChunk.length ? batchResponse[itemIndex] : undefined;

                const responseMatch = exactMatch ?? positionalMatch;
                if (responseMatch) {
                    lookup.set(requestedKey, responseMatch);
                }
            });

            // Add additional aliases so bare part numbers and returned product identifiers
            // can still resolve when the response search text differs from the CSV value.
            batchResponse.forEach(result => {
                const searchTextKey = this._normalizeSearchText(result.SearchText);
                if (searchTextKey && !lookup.has(searchTextKey)) {
                    lookup.set(searchTextKey, result);
                }

                result.Products?.forEach(product => {
                    const itemIdKey = this._normalizeSearchText(product.ItemId);
                    if (itemIdKey && !lookup.has(itemIdKey)) {
                        lookup.set(itemIdKey, result);
                    }

                    const productNumberKey = this._normalizeSearchText(product.ProductNumber);
                    if (productNumberKey && !lookup.has(productNumberKey)) {
                        lookup.set(productNumberKey, result);
                    }
                });
            });
        });

        return lookup;
    }

    /**
     * Handles data from the upload module.
     * @param items Items uploaded.
     * @private
     */
    private async _handleUploadedItems(items: IOrderUploadItem[]): Promise<void> {
        const requestedItemIds = items
            .map(item => this._getUploadedItemSearchText(item))
            .filter((itemId): itemId is string => Boolean(itemId));

        const uniqueItemIds = Array.from(
            new Map<string, string>(
                requestedItemIds.map(itemId => [this._normalizeSearchText(itemId), itemId] as [string, string])
            ).values()
        );

        if (uniqueItemIds.length === 0) {
            return;
        }

        const chunks: string[][] = [];
        for (let index = 0; index < uniqueItemIds.length; index += this._bulkUploadChunkSize) {
            chunks.push(uniqueItemIds.slice(index, index + this._bulkUploadChunkSize));
        }

        this.setState({ loading: true });

        try {
            const batchResponses = await this._mapWithConcurrency(chunks, this._bulkUploadConcurrency, (chunk, index) =>
                this._retryTransientRequest(() => this.getProductSearchResult(chunk), index + 1)
            );

            const responseByRequestedItemId = this._buildUploadedItemResponseLookup(chunks, batchResponses);

            const advancedNotes = (await getAdvancedNotesAsync({ callerContext: this.props.context.actionContext }, uniqueItemIds)) ?? [];

            let existingRows = [...this.state.rows];
            const firstEmptyRowIndex = existingRows.findIndex(row => !row.value);
            if (firstEmptyRowIndex >= 0) {
                existingRows = existingRows.slice(0, firstEmptyRowIndex);
            }

            let rowId = existingRows.reduce((maxId, row) => Math.max(maxId, row.id), 0) + 1;
            const successfulRows: IEveQuickOrderRowData[] = [];
            const failedRows: IEveQuickOrderRowData[] = [];

            items.forEach(item => {
                const requestedSearchText = this._getUploadedItemSearchText(item);
                const requestedSearchKey = this._normalizeSearchText(requestedSearchText);
                const partNumber = item.part_number?.toString() ?? '';
                const partNumberKey = this._normalizeSearchText(partNumber);

                const responseMatch = responseByRequestedItemId.get(requestedSearchKey) ?? responseByRequestedItemId.get(partNumberKey);

                const products = responseMatch?.Products ?? [];
                const foundProduct = ArrayExtensions.hasElements(products);
                const manufacturerCode = this._getManufacturerCodeForRow(products);
                const searchText = responseMatch?.SearchText || requestedSearchText || partNumber;
                const quantity = item.qty || 1;

                const rowData = this._generateRowData(
                    products,
                    rowId++,
                    manufacturerCode,
                    searchText,
                    advancedNotes,
                    foundProduct,
                    quantity
                );

                if (rowData.rowError === RowError.None) {
                    successfulRows.push(rowData);
                } else {
                    failedRows.push(rowData);
                }
            });

            const rows = existingRows.concat(successfulRows, failedRows);
            while (rows.length < this.props.config.defaultRows) {
                rows.push(this._generateEmptyRow(rowId++));
            }

            this.setState(
                {
                    rows,
                    loading: false,
                    prices: [],
                    hasUpdated: false,
                    updatedRows: new Set(),
                    shouldRowUpdate: false,
                    uploadedItems: true
                },
                () => {
                    void this.getProductPrices();
                }
            );
        } catch (error) {
            console.error('Quick Order bulk upload failed.', error);
            this.setState({ loading: false });
            throw error;
        }
    }

    private _generateRowData(
        products: SimpleProductExt[],
        rowId: number,
        manufacturerCode: string,
        searchText: string,
        advancedNotes: IAdvancedNoteEntity[],
        foundProduct: boolean,
        quantity: number
    ): IEveQuickOrderRowData {
        let rowError = RowError.None;

        if (!foundProduct || this.props.config.hiddenManufacturerCodes?.includes(manufacturerCode)) {
            rowError = RowError.ProductNotFound;
        } else if (products.length > 1 && manufacturerCode === '') {
            rowError = RowError.MultipleChoicesAvailable;
        } else if (quantity < 1) {
            rowError = RowError.InvalidQuantity;
        } else if (!products[0].IsEligible) {
            rowError = RowError.NotEligbile;
        } else if (products[0].IsUnavailable) {
            rowError = RowError.Unavailable;
        } else if (products[0].AdjustedPrice === 0) {
            rowError = RowError.ZeroPrice;
        }

        let quantityErrors: string[] = [];

        if (foundProduct && products.length > 0) {
            quantityErrors = this._checkQuantityBehavior(products[0].Behavior, quantity);
        }

        if (quantityErrors.length > 0 && rowError === RowError.None) {
            rowError = RowError.InvalidQuantity;
        }

        return {
            ...this._generateEmptyRow(rowId),
            product: foundProduct ? products[0] : null,
            availableProducts: products,
            value: this._stripManufacturerFromSearchText(manufacturerCode, searchText),
            quantity: quantity.toString(),
            rowError,
            quantityErrors,
            advancedNotes,
            manufacturerCode
        };
    }

    private _stripManufacturerFromSearchText(manufacturerCode: string, searchText: string) {
        if (manufacturerCode === '') {
            return searchText;
        }
        // eslint-disable-next-line security/detect-non-literal-regexp
        return searchText.replace(new RegExp(manufacturerCode, 'i'), '');
    }

    private _getManufacturerCodeForRow(products: SimpleProductExt[]): string {
        // 6273 - Auto select KOH when products returned are only MTD or KOH manufacturers
        const kohConstant = 'KOH';
        const mtdConstant = 'MTD';
        const prod = products && products[0];
        if (prod && products?.length === 1) {
            const mfrCodeVal = this._getExtensionPropertyValueByKey(
                prod.ExtensionProperties,
                this._manufacturerCodeExtensionPropertyKey,
                CommercePropertyValueEnum.string
            );
            return (mfrCodeVal as string) || '';
        } else if (products && products.length > 1) {
            // 6273 - Auto select KOH when products returned are only MTD or KOH manufacturers
            const onlyKohOrMtd = products.every(
                p =>
                    mtdConstant ===
                        this._getExtensionPropertyValueByKey(
                            p.ExtensionProperties,
                            this._manufacturerCodeExtensionPropertyKey,
                            CommercePropertyValueEnum.string
                        ) ||
                    kohConstant ===
                        this._getExtensionPropertyValueByKey(
                            p.ExtensionProperties,
                            this._manufacturerCodeExtensionPropertyKey,
                            CommercePropertyValueEnum.string
                        )
            );
            return onlyKohOrMtd ? kohConstant : '';
        }
        return '';
    }

    /**
     * Checks product Behavior for minimum, maximum, and multiple-of quantity rules.
     * Returns the specific RowError if violated, otherwise RowError.None.
     */
    private _checkQuantityBehavior(
        behavior: { MinimumQuantity?: number; MaximumQuantity?: number; MultipleOfQuantity?: number } | undefined,
        quantity: number
    ): string[] {
        const errors: string[] = [];
        if (!behavior) {
            return errors;
        }

        const { MinimumQuantity, MaximumQuantity, MultipleOfQuantity } = behavior;
        const minText = (this.props.config.quantityErrorMessage?.minimum ?? this.props.resources.quantityErrorMinimumText ?? '').trimEnd();
        const maxText = (this.props.config.quantityErrorMessage?.maximum ?? this.props.resources.quantityErrorMaximumText ?? '').trimEnd();
        const mulText = (
            this.props.config.quantityErrorMessage?.multiple ??
            this.props.resources.quantityErrorMultipleText ??
            ''
        ).trimEnd();

        if (MinimumQuantity && MinimumQuantity > 0 && quantity < MinimumQuantity) {
            errors.push(`${minText} ${behavior.MinimumQuantity}`);
        }
        if (MaximumQuantity && MaximumQuantity > 0 && quantity > MaximumQuantity) {
            errors.push(`${maxText} ${behavior.MaximumQuantity}`);
        }
        if (MultipleOfQuantity && MultipleOfQuantity > 0 && quantity % MultipleOfQuantity !== 0) {
            errors.push(`${mulText} ${behavior.MultipleOfQuantity}`);
        }

        return errors;
    }

    /**
     * Renders the quick order table
     * @private
     */
    private _renderTable() {
        const {
            resources: {
                manufacturerCodeTableHeading,
                partNumberTableHeading,
                quantityTableHeading,
                itemNameHeading,
                priceHeading,
                deleteHeading,
                availabilityHeading,
                yourPrice
            }
        } = this.props;
        const rows = [
            manufacturerCodeTableHeading,
            partNumberTableHeading,
            quantityTableHeading,
            itemNameHeading,
            priceHeading,
            yourPrice,
            availabilityHeading,
            deleteHeading
        ];

        return (
            <div>
                <ul className={'q-table'}>
                    <li className='q-table-header'>
                        {rows.map((row, i) => (
                            <div key={row} className={`q-col q-col-${i + 1}`}>
                                {row}
                            </div>
                        ))}
                    </li>
                    {this.state.rows.map((r, i) => {
                        return (
                            <li className={`q-table-row ${r.rowError !== RowError.None && 'q-table-row--error'}`} key={r.id}>
                                {this._generateRow(r, i === this.state.rows.length - 1)}
                            </li>
                        );
                    })}
                    <li className='q-table-row'>{this._renderButtonRow()}</li>
                </ul>
            </div>
        );
    }

    /**
     * Handles removing a row
     * @param id id to remove
     * @private
     */
    private _handleRowDelete(id: number) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(r => r.id === id);
        if (idx > -1) {
            rows.splice(idx, 1);
            const newRowId = rows[rows.length - 1].id + 1;
            if (rows.length < this.props.config.defaultRows) {
                rows.push(this._generateEmptyRow(newRowId));
            }
            this.setState({ rows });
        }
    }

    /**
     * Generates an empty row
     * @param id id to initialize row with
     * @private
     */
    private _generateEmptyRow(id: number) {
        return {
            id,
            value: '',
            quantity: '1',
            product: null,
            availableProducts: [],
            rowError: RowError.None,
            quantityErrors: [],
            manufacturerCode: '',
            advancedNotes: []
        };
    }

    /**
     * Updates the product in a row
     * @param id id of row to update
     * @param products product to add to row
     * @private
     */
    private _updateRowProduct(
        id: number,
        products: SimpleProductExt[],
        notes: IAdvancedNoteEntity[],
        searchText: string,
        quantity: string
    ) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(row => row.id === id);
        const manufacturerCode = this._getManufacturerCodeForRow(products);
        rows[idx] = this._generateRowData(products || [], rows[idx].id, manufacturerCode, searchText, notes, true, Number(quantity));

        this.setState(
            prevState => {
                const updatedRows = new Set(prevState.updatedRows);
                updatedRows.add(id);

                return {
                    rows,
                    updatedRows,
                    shouldRowUpdate: true
                };
            },
            () => {
                void this.getUpdatedProductPrice();
            }
        );
    }

    private _updateRowErrorState(row: IEveQuickOrderRowData): RowError {
        const {
            config: { maxQuantity }
        } = this.props;
        let rowError: RowError = RowError.None;

        if (row.value && !row.product) {
            rowError = RowError.ProductNotFound;
        } else if (
            Number(row.quantity) === 0 ||
            Number.isNaN(Number(row.quantity)) ||
            Number(row.quantity) < 1 ||
            Number(row.quantity) > (maxQuantity || 1000)
        ) {
            rowError = RowError.InvalidQuantity;
        } else if (row.product?.IsEligible === false) {
            rowError = RowError.NotEligbile;
        } else if (row.product?.IsUnavailable) {
            rowError = RowError.Unavailable;
        } else if (Number(row.quantity) < 1) {
            rowError = RowError.InvalidQuantity;
        } else if (row.product?.AdjustedPrice === 0) {
            rowError = RowError.ZeroPrice;
        }

        if (row.product) {
            const quantityErrors = this._checkQuantityBehavior(row.product.Behavior, Number(row.quantity));

            row.quantityErrors = quantityErrors;

            if (quantityErrors.length > 0 && rowError === RowError.None) {
                rowError = RowError.InvalidQuantity;
            }
        }

        return rowError;
    }
    /**
     * Updates value or quantity of row
     * @param id id of row to update
     * @param value value to update
     * @param quantity quantity to update
     * @param manufacturerCode Selected manufacturer code
     * @private
     */
    private _updateRowValue(id: number, value: string, quantity: string, manufacturerCode: string) {
        const rows = [...this.state.rows];
        const idx = rows.findIndex(row => row.id === id);

        if (idx < 0) {
            return;
        }

        const hiddenManufacturerCodes: string[] = this.props.config.hiddenManufacturerCodes || [];
        const partNumberPrefix = value.substring(0, 3).toUpperCase();
        let shouldRefreshPrice = false;

        if (hiddenManufacturerCodes.includes(partNumberPrefix)) {
            rows[idx].product = null;
            rows[idx].rowError = RowError.ProductNotFound;
        } else {
            if (rows[idx].value !== value) {
                rows[idx].product = null;
                rows[idx].manufacturerCode = 'KOH';
                rows[idx].quantity = quantity;
                this._getProductByRowQuery(id, value, manufacturerCode);
            }

            if (rows[idx].quantity !== quantity) {
                rows[idx].quantity = quantity;
                rows[idx].rowError = this._updateRowErrorState(rows[idx]);
            }

            if (rows[idx].manufacturerCode !== manufacturerCode) {
                rows[idx].manufacturerCode = manufacturerCode;
                rows[idx].rowError = this._updateRowErrorState(rows[idx]);

                if (rows[idx].availableProducts.length > 1) {
                    rows[idx].product = this._getProductManufacturerById(manufacturerCode, rows[idx].availableProducts);
                    rows[idx].rowError = this._updateRowErrorState(rows[idx]);
                    shouldRefreshPrice = true;
                } else if (rows[idx].value !== '') {
                    this._getProductByRowQuery(id, value, manufacturerCode);
                }
            }
        }

        rows[idx].value = value;
        rows[idx].quantity = quantity;
        rows[idx].manufacturerCode = manufacturerCode;

        this.setState(
            prevState => {
                const updatedRows = new Set(prevState.updatedRows);
                if (shouldRefreshPrice) {
                    updatedRows.add(id);
                }

                return {
                    rows,
                    updatedRows,
                    shouldRowUpdate: shouldRefreshPrice
                };
            },
            () => {
                if (shouldRefreshPrice) {
                    void this.getUpdatedProductPrice();
                }
            }
        );
    }

    private _getProductManufacturerById(mfrCode: string, products: SimpleProductExt[]) {
        let _product = products[0];

        products.forEach(pr => {
            const mfrCodeVal = this._getExtensionPropertyValueByKey(
                pr.ExtensionProperties,
                this._manufacturerCodeExtensionPropertyKey,
                CommercePropertyValueEnum.string
            );
            // compare the selected mfrCode with the original item's manufacturer's code stored as an extension property
            if (mfrCode === (mfrCodeVal as string) || '') {
                _product = pr;
            }
        });

        return _product;
    }

    /**
     * Calls the api when a value is updated
     * @param id id of row to update
     * @param searchText text to search for
     * @param manufacturerCode
     * @private
     */
    private _getProductByRowQuery(id: number, searchText: string, manufacturerCode: string) {
        // 6297 - Made the searchText length check that it is greater than or equal to the minimum query length so that it makes more sense to the user in site builder.
        if (searchText.length > (this.props.config.minQueryLength || this._defaultMinQueryLength)) {
            let textToSearch = searchText;
            if (manufacturerCode) {
                textToSearch = manufacturerCode + textToSearch;
            }
            this.state.debounce({ searchText: [textToSearch], id });
        }
    }

    /**
     * Adds two blank rows
     * @private
     */
    private _addTwoRows() {
        const rows = [...this.state.rows];

        const maxRowId = rows.reduce((max, row) => (row.id > max ? row.id : max), 0);

        const newRowId = maxRowId + 1;
        rows.push(this._generateEmptyRow(newRowId));
        rows.push(this._generateEmptyRow(newRowId + 1));

        this.setState({ rows });
    }

    /**
     * Fetches prices for unique products in the current rows.
     *
     * This function filters the rows to extract unique products based on their RecordId,
     * and then calls the `getPriceForProductSearchResultAction` to fetch their prices.
     * If successful, it updates the component state with the retrieved prices and flags
     * that the state has been updated. Errors encountered during the process are logged
     * to the console.
     *
     * @returns {Promise<void>} Resolves after the state is updated or exits early if no products are present.
     */
    private async getProductPrices(): Promise<void> {
        const products = Array.from(
            new Map<number, SimpleProductExt>(
                this.state.rows
                    .filter(row => Boolean(row.product))
                    .map(row => [row.product!.RecordId, row.product!] as [number, SimpleProductExt])
            ).values()
        );

        if (products.length === 0) {
            this.setState({
                prices: [],
                hasUpdated: true,
                updatedRows: new Set(),
                shouldRowUpdate: false
            });
            return;
        }

        try {
            const input = new PriceForProductSearchResultInput(products);
            const prices = await getPriceForProductSearchResultAction(input, this.props.context.actionContext);

            this.setState({
                prices,
                hasUpdated: true,
                updatedRows: new Set(),
                shouldRowUpdate: false
            });
        } catch (error) {
            console.error('Error fetching product prices:', error);
            this.setState({ hasUpdated: true });
        }
    }

    private async getUpdatedProductPrice(): Promise<void> {
        const updatedRowIds = Array.from(this.state.updatedRows);
        if (updatedRowIds.length === 0) {
            this.setState({ shouldRowUpdate: false });
            return;
        }

        const updatedProducts = Array.from(
            new Map<number, SimpleProductExt>(
                this.state.rows
                    .filter(row => updatedRowIds.includes(row.id) && Boolean(row.product))
                    .map(row => [row.product!.RecordId, row.product!] as [number, SimpleProductExt])
            ).values()
        );

        if (updatedProducts.length === 0) {
            this.setState({
                updatedRows: new Set(),
                shouldRowUpdate: false
            });
            return;
        }

        try {
            const input = new PriceForProductSearchResultInput(updatedProducts);
            const updatedPrices = await getPriceForProductSearchResultAction(input, this.props.context.actionContext);

            this.setState(prevState => {
                const pricesByRecordId = new Map<number, SimpleProductExt>(
                    prevState.prices.map(product => [product.RecordId, product] as [number, SimpleProductExt])
                );

                updatedPrices.forEach(product => pricesByRecordId.set(product.RecordId, product));

                return {
                    prices: Array.from(pricesByRecordId.values()),
                    updatedRows: new Set(),
                    shouldRowUpdate: false
                };
            });
        } catch (error) {
            console.error('Error fetching updated product prices:', error);
            this.setState({
                updatedRows: new Set(),
                shouldRowUpdate: false
            });
        }
    }

    /**
     * To handle paragraph change.
     * @param event - To handle text change event.
     * @name - HandleParagraphChange
     * @public
     * @returns - The Paragraph value.
     */
    public handleGuideTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.orderUploadGuideText = event.target.value);

    public render(): JSX.Element | null {
        const { orderUploadGuideText } = this.props.config;
        const guideText = orderUploadGuideText && (
            <Msdyn365.RichTextComponent
                text={orderUploadGuideText}
                className='eve-quick-order-text-wrapper--text'
                editProps={{ onEdit: this.handleGuideTextChange, requestContext: this.props.context.request }}
            />
        );
        const viewProps: IEveQuickOrderViewProps = {
            ...this.props,
            handleUploadedItems: this._handleUploadedItems,
            quickOrderTable: this._renderTable(),
            quickOrderHeader: this._renderHeading(),
            quickOrderWrapper: {
                className: classNames('eve-quick-order'),
                tag: 'div',
                moduleProps: this.props
            },
            orderUploadWrapper: {
                tag: 'div',
                className: 'qo-order-upload-wrapper'
            },
            guideText: guideText
        };
        return this.props.renderView(viewProps);
    }
}

export default EveQuickOrder;
